import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import CrmAvatar from '../../shared/components/CrmAvatar';

const axios = require('axios').default;

// Setlist tokens — resolve to light/dark automatically under body.od-themed.
const T = {
  textPrimary: 'var(--sl-text-primary)',
  textSecondary: 'var(--sl-text-secondary)',
  textTertiary: 'var(--sl-text-tertiary)',
  brand: 'var(--sl-fg-brand)',
  brandBg: 'var(--sl-bg-brand-primary)',
  brandBorder: 'var(--sl-border-brand)',
  cardBg: 'var(--sl-bg-primary)',
  panelBg: 'var(--sl-bg-secondary)',
  border: 'var(--sl-border-secondary)',
  radius: 'var(--sl-radius-lg, 12px)',
};

// Fields the chooser can resolve. address is a composite; email/phone become
// primary with the rest kept as secondary. Keys map to Fan::STRONG_PARAMS.
const MERGE_FIELDS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Primary email', kind: 'email' },
  { key: 'phone_number', label: 'Primary phone', kind: 'phone' },
  { key: 'title', label: 'Title' },
  { key: 'company_name', label: 'Company' },
  { key: 'contact_type', label: 'Contact type' },
  { key: 'address', label: 'Mailing address', kind: 'address', columns: ['address1', 'address2', 'city', 'state', 'zip'] },
];

const digits = (v) => String(v || '').replace(/\D/g, '');
const formatCard = (c) => `${c.brand ? `${c.brand.replace(/\b\w/g, (l) => l.toUpperCase())} ` : ''}••${c.last4}`;
const addedLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Display + comparison value for a contact/field.
function displayValue(contact, field) {
  if (field.kind === 'address') {
    return [contact.address1, contact.address2, [contact.city, contact.state].filter(Boolean).join(', '), contact.zip]
      .filter(Boolean).join(', ');
  }
  if (field.kind === 'phone') return contact.human_phone || contact.phone_number || '';
  return contact[field.key] || '';
}
function compareValue(contact, field) {
  if (field.kind === 'phone') return digits(displayValue(contact, field));
  return displayValue(contact, field).trim().toLowerCase();
}

function MergeContactsModal({ contacts: initialContacts, team, csrfToken, onMerged, onClose }) {
  // Cards + order counts aren't in the contacts-list :list payload, so when the
  // modal is opened from the list multi-select we fetch them (the duplicates
  // page already supplies them). Held in state so the preview fills in on arrival.
  const [contacts, setContacts] = useState(initialContacts);
  const [primaryId, setPrimaryId] = useState(initialContacts[0] && initialContacts[0].id);
  // choices[fieldKey] = the record id whose value to keep for that field.
  const [choices, setChoices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setContacts(initialContacts);
    const missing = initialContacts.some((c) => c.cards == null || c.orders_count == null);
    if (!missing || !team || !team.id) return undefined;

    let active = true;
    axios.get(`/teams/${team.id}/crm/fans/merge_preview.json`, {
      params: { contact_ids: initialContacts.map((c) => c.id) },
    })
      .then((res) => {
        if (!active) return;
        const byId = {};
        (res.data || []).forEach((entry) => { byId[entry.id] = entry; });
        setContacts(initialContacts.map((c) => ({ ...c, ...(byId[c.id] || {}) })));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [initialContacts, team && team.id]);

  const primary = contacts.find((c) => c.id === primaryId) || contacts[0];

  // Classify each field: conflict (choose), single-value (kept automatically),
  // or absent everywhere (skip).
  const classified = MERGE_FIELDS.map((field) => {
    const perRecord = contacts.map((c) => ({ id: c.id, value: displayValue(c, field), cmp: compareValue(c, field) }));
    const withValue = perRecord.filter((r) => r.value);
    const distinct = [...new Set(withValue.map((r) => r.cmp))];
    return { field, perRecord, withValue, conflict: distinct.length > 1 };
  });
  const conflicts = classified.filter((c) => c.conflict);
  const kept = classified.filter((c) => !c.conflict && c.withValue.length > 0);

  // The chosen record id for a field (defaults to the primary if it has a value,
  // else the first record that does).
  const chosenRecordId = (fieldKey) => {
    if (choices[fieldKey] && contacts.some((c) => c.id === choices[fieldKey])) return choices[fieldKey];
    const c = classified.find((x) => x.field.key === fieldKey);
    const primaryHas = c && c.perRecord.find((r) => r.id === primaryId && r.value);
    return primaryHas ? primaryId : (c && c.withValue[0] && c.withValue[0].id);
  };

  const chosenRecord = (fieldKey) => contacts.find((c) => c.id === chosenRecordId(fieldKey)) || primary;

  // ---- Preview values ----
  const mergedValue = (field) => displayValue(chosenRecord(field.key), field);
  const previewName = [mergedValue(MERGE_FIELDS[0]), mergedValue(MERGE_FIELDS[1])].filter(Boolean).join(' ');
  // A contact stores one primary + two secondary emails, one primary + one
  // secondary phone. Cap the preview to what actually persists and count the
  // rest as dropped, so the UI never promises data the merge won't keep.
  const KEPT_SECONDARY_EMAILS = 2;
  const KEPT_SECONDARY_PHONES = 1;

  // The server fills secondary slots from [chosen] + [winner, ...losers], so the
  // preview must walk the candidates in that same order (primary first) or it
  // can show the wrong secondaries when the chosen primary isn't the first record.
  const orderedContacts = [primary, ...contacts.filter((c) => c.id !== primary.id)];

  const previewEmail = chosenRecord('email').email || primary.email;
  const distinctSecondaryEmails = [...new Set(
    orderedContacts.flatMap((c) => [c.email, c.email_secondary_1, c.email_secondary_2]).filter(Boolean).map((e) => e.trim()),
  )].filter((e) => e.toLowerCase() !== String(previewEmail || '').toLowerCase());
  const secondaryEmails = distinctSecondaryEmails.slice(0, KEPT_SECONDARY_EMAILS);
  const droppedEmailCount = Math.max(distinctSecondaryEmails.length - KEPT_SECONDARY_EMAILS, 0);

  const previewPhoneRecord = chosenRecord('phone_number');
  const previewPhone = previewPhoneRecord.human_phone || previewPhoneRecord.phone_number || '';
  const primaryPhoneDigits = String(previewPhoneRecord.phone_number || '').replace(/\D/g, '');
  const distinctSecondaryPhones = [...new Set(
    orderedContacts.flatMap((c) => [c.phone_number, c.phone_secondary_1]).filter(Boolean).map((p) => String(p).replace(/\D/g, '')),
  )].filter((p) => p && p !== primaryPhoneDigits);
  const droppedPhoneCount = Math.max(distinctSecondaryPhones.length - KEPT_SECONDARY_PHONES, 0);
  const allCards = [...new Map(
    contacts.flatMap((c) => c.cards || []).map((card) => [`${card.brand}-${card.last4}`, card]),
  ).values()].map(formatCard);

  // Same/only-one label for the kept-automatically section.
  const keptNote = (entry) => (entry.withValue.length === contacts.length
    ? 'same on every record'
    : `only ${entry.withValue.length === 1 ? 'one record has' : 'some records have'} this`);

  const submit = async () => {
    if (!team || !team.id || !primaryId || contacts.length < 2) return;
    try {
      setSubmitting(true);
      setError(null);
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const overrides = {};
      conflicts.forEach(({ field }) => {
        const record = chosenRecord(field.key);
        if (field.kind === 'address') {
          field.columns.forEach((col) => { overrides[col] = record[col] || ''; });
        } else {
          overrides[field.key] = record[field.key];
        }
      });

      const ids = contacts.map((c) => c.id);
      const response = await axios.post(`/teams/${team.id}/crm/fans/merge.json`, {
        primary_contact_id: primaryId,
        contact_ids: ids,
        override_attributes: overrides,
      });

      const merged = response.data;
      const removedIds = ids.filter((id) => id !== merged.id);
      toast.success(`${removedIds.length + 1} contacts merged into ${merged.name || merged.email || 'contact'}.`);
      onMerged(merged, removedIds);
    } catch (err) {
      const resp = err.response;
      setError(resp && Array.isArray(resp.data) ? resp.data.join(', ') : 'Failed to merge contacts');
    } finally {
      setSubmitting(false);
    }
  };

  if (contacts.length < 2) return null;

  const gridCols = `150px repeat(${contacts.length}, minmax(0, 1fr))`;
  const sameName = (() => {
    const names = contacts.map((c) => [c.first_name, c.last_name].filter(Boolean).join(' ').trim().toLowerCase());
    return names.every((n) => n && n === names[0]) ? [contacts[0].first_name, contacts[0].last_name].filter(Boolean).join(' ') : null;
  })();

  return (
    <div className="team-modal-overlay">
      <div className="team-modal" style={{ maxWidth: 920 }}>
        <div className="team-modal-header">
          <h3 className="team-modal-title">
            Merge {contacts.length} contacts{sameName ? ` named "${sameName}"` : ''}
          </h3>
          <button type="button" className="team-modal-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="team-modal-body">
          {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

          <p style={{ color: T.textSecondary }}>
            Pick the primary contact, then choose which value to keep for any field that differs.
            Everything (orders, tickets, notes) moves onto the primary. Emails and phone numbers you
            don&apos;t pick are kept as secondary.
          </p>

          {/* Record selector */}
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, alignItems: 'stretch', marginBottom: 16 }}>
            <div style={{ alignSelf: 'center', color: T.textTertiary, fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>PRIMARY</div>
            {contacts.map((c, i) => {
              const active = primaryId === c.id;
              return (
                <label
                  key={c.id}
                  style={{
                    border: `1px solid ${active ? T.brandBorder : T.border}`,
                    background: active ? T.brandBg : T.cardBg,
                    borderRadius: 8, padding: '8px 10px', cursor: 'pointer', margin: 0,
                  }}
                >
                  <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <input type="radio" name="primary-record" checked={active} onChange={() => { setPrimaryId(c.id); setChoices({}); }} />
                    <span style={{ fontWeight: 600, color: T.textPrimary }}>Record {i + 1}</span>
                    {active && <span style={{ color: T.brand, fontSize: 12, fontWeight: 600 }}>Primary</span>}
                  </div>
                  <div style={{ color: T.textSecondary, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.email}>{c.email}</div>
                  <div style={{ color: T.textTertiary, fontSize: 12 }}>
                    {c.orders_count != null ? `${c.orders_count} order${c.orders_count === 1 ? '' : 's'}` : ''}
                    {addedLabel(c.created_at) ? `${c.orders_count != null ? ' · ' : ''}added ${addedLabel(c.created_at)}` : ''}
                  </div>
                </label>
              );
            })}
          </div>

          {/* Choose what to keep */}
          {conflicts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: T.textTertiary, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 6 }}>CHOOSE WHAT TO KEEP</div>
              {conflicts.map(({ field, perRecord }) => (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ color: T.textSecondary, fontSize: 13 }}>{field.label}</div>
                  {perRecord.map((r) => {
                    const selected = chosenRecordId(field.key) === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setChoices((prev) => ({ ...prev, [field.key]: r.id }))}
                        disabled={!r.value}
                        title={r.value}
                        style={{
                          textAlign: 'left',
                          border: `1px solid ${selected ? T.brandBorder : T.border}`,
                          background: selected ? T.brandBg : T.cardBg,
                          color: r.value ? T.textPrimary : T.textTertiary,
                          borderRadius: 8, padding: '8px 10px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {selected && <i className="fa fa-check mr-1" style={{ color: T.brand }} />}
                        {r.value || '—'}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Kept automatically */}
          {(kept.length > 0 || allCards.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: T.textTertiary, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 6 }}>KEPT AUTOMATICALLY</div>
              {kept.map((entry) => (
                <div key={entry.field.key} className="d-flex justify-content-between" style={{ fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: T.textSecondary }}>
                    <span style={{ display: 'inline-block', minWidth: 130, color: T.textTertiary }}>{entry.field.label}</span>
                    {entry.withValue[0].value}
                  </span>
                  <span style={{ color: T.textTertiary }}>{keptNote(entry)}</span>
                </div>
              ))}
              {allCards.length > 0 && (
                <div className="d-flex justify-content-between" style={{ fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: T.textSecondary }}>
                    <span style={{ display: 'inline-block', minWidth: 130, color: T.textTertiary }}>Payment cards</span>
                    {allCards.join(', ')}
                  </span>
                  <span style={{ color: T.textTertiary }}>all cards are kept</span>
                </div>
              )}
            </div>
          )}

          {/* After merging preview */}
          <div style={{ background: T.panelBg, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16 }}>
            <div style={{ color: T.textTertiary, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              <i className="fal fa-code-merge mr-1" />After merging
            </div>
            <div className="d-flex align-items-center mb-3" style={{ gap: 10 }}>
              <CrmAvatar firstName={mergedValue(MERGE_FIELDS[0])} lastName={mergedValue(MERGE_FIELDS[1])} avatarUrl={primary.avatar_thumb_url} size={32} className="team-avatar-sm" />
              <div>
                <div style={{ fontWeight: 600, color: T.textPrimary }}>{previewName || '(No name)'}</div>
                <div style={{ color: T.textTertiary, fontSize: 13 }}>{previewEmail || '—'}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 24px', fontSize: 13 }}>
              <PreviewField label="First name" value={mergedValue(MERGE_FIELDS[0])} />
              <PreviewField label="Last name" value={mergedValue(MERGE_FIELDS[1])} />
              <PreviewField label="Primary email" value={previewEmail} />
              <PreviewField label="Secondary emails" value={secondaryEmails.join(', ')} />
              <PreviewField label="Primary phone" value={previewPhone} />
              <PreviewField label="Mailing address" value={mergedValue(MERGE_FIELDS[7])} />
              <PreviewField label="Payment cards" value={allCards.join(', ')} />
            </div>
            {(droppedEmailCount > 0 || droppedPhoneCount > 0) && (
              <div style={{ color: T.textTertiary, fontSize: 12, marginTop: 10 }}>
                {droppedEmailCount > 0 && `${droppedEmailCount} more email${droppedEmailCount === 1 ? '' : 's'} won't be kept (max two secondary emails). `}
                {droppedPhoneCount > 0 && `${droppedPhoneCount} more phone number${droppedPhoneCount === 1 ? '' : 's'} won't be kept (one secondary phone).`}
              </div>
            )}
            <div style={{ color: T.textTertiary, fontSize: 12, marginTop: 10 }}>
              All orders, tickets and notes from the other {contacts.length - 1} contact{contacts.length - 1 === 1 ? '' : 's'} move here.
            </div>
          </div>
        </div>

        <div className="team-modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={submitting || !primaryId} onClick={submit}>
            {submitting ? 'Merging…' : `Merge ${contacts.length} contacts`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewField({ label, value }) {
  return (
    <div>
      <div style={{ color: 'var(--sl-text-tertiary)', fontSize: 12 }}>{label}</div>
      <div style={{ color: 'var(--sl-text-primary)' }}>{value || '—'}</div>
    </div>
  );
}

export default MergeContactsModal;
